import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, ChevronLeft, ChevronRight, Lock } from "lucide-react";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
} from "date-fns";

export default function ServiceCalendar({
  appointments = [],
  unavailability = [],
  selectedDate,
  onDateSelect,
}) {
  const [currentMonth, setCurrentMonth] = React.useState(
    selectedDate || new Date()
  );

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);

  // Get the start and end of the calendar grid (including days from prev/next months)
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);

  // Get all days to display in the calendar grid
  const calendarDays = eachDayOfInterval({
    start: calendarStart,
    end: calendarEnd,
  });

  const getAppointmentsForDay = (date) => {
    if (!Array.isArray(appointments)) return [];

    return appointments.filter((apt) => {
      try {
        if (!apt || !apt.appointment_date) return false;

        return isSameDay(new Date(apt.appointment_date), date);
      } catch (error) {
        console.error("Error getting appointments for day:", error);
        return false;
      }
    });
  };

  const getUnavailabilityForDay = (date) => {
    if (!Array.isArray(unavailability)) return [];
    return unavailability.filter((u) => {
      try {
        if (!u || !u.start_time) return false;
        return isSameDay(new Date(u.start_time), date);
      } catch (error) {
        console.error("Error getting unavailability for day:", error);
        return false;
      }
    });
  };

  const handlePreviousMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const handleDateClick = (date) => {
    if (onDateSelect) {
      onDateSelect(date);
    }
  };

  return (
    <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-xl">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            {format(currentMonth, "MMMM yyyy")}
          </CardTitle>
          <div className="flex gap-2">
            <button
              onClick={handlePreviousMonth}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={handleNextMonth}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-7 gap-1 mb-4">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <div
              key={day}
              className="p-2 text-center text-sm font-medium text-slate-600"
            >
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {calendarDays.map((day) => {
            const dayAppointments = getAppointmentsForDay(day);
            const dayUnavailability = getUnavailabilityForDay(day);
            const isSelected = selectedDate && isSameDay(day, selectedDate);
            const isToday = isSameDay(day, new Date());
            const isCurrentMonth = isSameMonth(day, currentMonth);

            return (
              <div
                key={day.toString()}
                onClick={() => handleDateClick(day)}
                className={`
                  p-2 min-h-[60px] border border-slate-200 cursor-pointer hover:bg-blue-50 transition-colors
                  ${isSelected ? "bg-blue-100 border-blue-300" : ""}
                  ${isToday ? "ring-2 ring-blue-400" : ""}
                  ${!isCurrentMonth ? "opacity-50" : ""}
                `}
              >
                <div
                  className={`text-sm font-medium ${isToday ? "text-blue-600" : "text-slate-900"}`}
                >
                  {format(day, "d")}
                </div>
                <div className="mt-1 flex flex-col items-start gap-1">
                  {dayAppointments.length > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {dayAppointments.length} apt
                      {dayAppointments.length !== 1 ? "s" : ""}
                    </Badge>
                  )}
                  {dayUnavailability.length > 0 && (
                    <Badge
                      variant="destructive"
                      className="text-xs bg-gray-500 hover:bg-gray-600"
                    >
                      <Lock className="w-3 h-3" />
                    </Badge>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
